/**
 * Embedded-barcode ("scale barcode") parsing — the legacy POS's
 * `PosBarcodeScanerConfiguration` feature.
 *
 * A shop that weighs its goods prints its own label: one barcode that carries
 * BOTH the item code and the weight/quantity. Scanning it must ring up that
 * item at that quantity, not search for the digits.
 *
 * The layout is per subsidiary and is described entirely by digit positions,
 * which is why the settings are six numbers and nothing else. For AlSaqi:
 *
 *   total 11 · code 6 digits from 1 · qty 5 digits from 7 · 2 of them whole
 *
 *   0 0 8 1 3 1   0 3 5 0 0
 *   └── code ──┘  └┬┘└─┬──┘
 *      008131      03  .500     →  item 008131, qty 3.5
 *
 * ## The length gate is the whole discriminator
 *
 * A scan is treated as an embedded barcode ONLY when its length equals
 * `totalDigits` exactly. Anything else — a 13-digit EAN off a manufacturer's
 * pack, a typed item code, a product name — falls through to the ordinary
 * search, which is what `parseEmbeddedBarcode` returning null means. That is
 * the legacy behaviour (`order_events_definitions.js:2607`) and it is what
 * lets a shop run both kinds of label side by side: AlSaqi's own labels are 11
 * digits and every supplier barcode on their shelves is 13.
 *
 * ## The item code is matched LITERALLY, zero padding included
 *
 * The extracted code is compared to `Inv_Items.Code` as text. Matix pads item
 * codes to a fixed width — 2,638 of AlSaqi's 2,769 items are exactly six
 * characters, `'008131'` — so the padding in the barcode is the padding in the
 * table and stripping it would break the match rather than fix it.
 */

/**
 * One subsidiary's barcode layout, as stored in
 * `PosBarcodeScanerConfiguration`. Every field is nullable there, and an
 * unconfigured tenant has no row at all, so every field is nullable here too.
 */
export interface BarcodeScannerConfig {
  /**
   * Master gate — `mstr_ConfigurationParameter.Barcodescannersetting`, shared
   * with the legacy POS and the ERP. The digits below are ignored while it is
   * off, so a half-filled layout can be saved without affecting a live till.
   */
  enabled: boolean;
  /** Total length of the label. A scan of any other length is not one of these. */
  totalDigits: number | null;
  /** How many digits the item code occupies. */
  itemCodeDigits: number | null;
  /** 1-based position where the item code starts. */
  itemCodeStart: number | null;
  /** How many digits the quantity occupies IN TOTAL (whole + fraction). */
  qtyDigits: number | null;
  /** 1-based position where the quantity starts. */
  qtyStart: number | null;
  /**
   * How many of `qtyDigits` are the WHOLE part; the rest are the fraction.
   * With 5 qty digits and 2 whole, `03500` is 3.500.
   */
  qtyWholeDigits: number | null;
}

/** An empty layout — what an unconfigured subsidiary reads as. */
export const EMPTY_BARCODE_SCANNER_CONFIG: BarcodeScannerConfig = {
  enabled: false,
  totalDigits: null,
  itemCodeDigits: null,
  itemCodeStart: null,
  qtyDigits: null,
  qtyStart: null,
  qtyWholeDigits: null,
};

export interface ParsedEmbeddedBarcode {
  /** The item code as printed, padding included — match it against `Code`. */
  itemCode: string;
  /** The decoded quantity. 1 when the layout carries no quantity field. */
  qty: number;
}

function positive(n: number | null | undefined): number | null {
  return typeof n === 'number' && Number.isFinite(n) && n > 0 ? Math.trunc(n) : null;
}

/**
 * True when this layout is complete enough to parse a scan with.
 *
 * The quantity half is optional — a layout that only carries an item code is
 * legitimate, and reads as quantity 1 — but the code half and the total length
 * are not, because without them there is nothing to extract and no way to tell
 * an embedded barcode from an ordinary one.
 */
export function isBarcodeScannerConfigured(cfg: BarcodeScannerConfig): boolean {
  const total = positive(cfg.totalDigits);
  const codeLen = positive(cfg.itemCodeDigits);
  const codeStart = positive(cfg.itemCodeStart);
  if (total === null || codeLen === null || codeStart === null) return false;
  // The code field has to fit inside the label, or every scan misses.
  return codeStart - 1 + codeLen <= total;
}

/**
 * Decode a scanned string into an item code and a quantity, or null when it is
 * not one of this tenant's embedded barcodes (wrong length, feature off, or
 * layout incomplete) — in which case the caller does its ordinary search.
 *
 * Deliberately the same arithmetic as the legacy POS, including the fact that
 * the quantity's decimal point is positional rather than printed. The one
 * place it differs: legacy yields `NaN` for a quantity field that falls off
 * the end of the label, and that NaN reaches the cart. Here a missing or
 * unreadable quantity reads as 1 — the same thing a layout with no quantity
 * field does.
 */
export function parseEmbeddedBarcode(
  raw: string,
  cfg: BarcodeScannerConfig,
): ParsedEmbeddedBarcode | null {
  if (!cfg.enabled || !isBarcodeScannerConfigured(cfg)) return null;

  const scan = raw.trim();
  if (scan.length !== positive(cfg.totalDigits)) return null;

  const codeStart = positive(cfg.itemCodeStart)! - 1;
  const codeLen = positive(cfg.itemCodeDigits)!;
  const itemCode = scan.slice(codeStart, codeStart + codeLen);
  if (!itemCode) return null;

  return { itemCode, qty: readQty(scan, cfg) };
}

/**
 * The quantity slice, with its implied decimal point applied.
 *
 * `qtyWholeDigits` splits the field: the first N digits are units, the rest are
 * the fraction. A field no longer than N is read whole — that is how a layout
 * with, say, 3 qty digits all whole encodes a plain count.
 */
function readQty(scan: string, cfg: BarcodeScannerConfig): number {
  const qtyStart = positive(cfg.qtyStart);
  const qtyLen = positive(cfg.qtyDigits);
  if (qtyStart === null || qtyLen === null) return 1;

  const field = scan.slice(qtyStart - 1, qtyStart - 1 + qtyLen);
  if (!/^\d+$/.test(field)) return 1;

  const whole = positive(cfg.qtyWholeDigits);
  const text =
    whole !== null && field.length > whole
      ? `${field.slice(0, whole)}.${field.slice(whole)}`
      : field;

  const qty = Number(text);
  // A label that encodes zero is a misprint, not an instruction to add nothing.
  return Number.isFinite(qty) && qty > 0 ? qty : 1;
}
